import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { stringIsNumber } from 'helpers/helpers';
import { ParameterListInputProps } from './types';

const ParameterListInput = ({
    value,
    setParameterValue,
    parameterName,
    parameterType,
}: ParameterListInputProps): React.ReactElement => {
    // local state because debounce on setParameterValue disable real-time modification
    const [inputValues, setInputValues] = useState(value);
    const [isError, setIsError] = useState(false);
    const { t } = useTranslation();

    // @ts-expect-error [TS migration] Was not detected by flow
    const filterEnteredValueType = (listItem) => {
        const defaultCondition = !!listItem;
        switch (parameterType) {
            case 'list[number]': {
                return defaultCondition && stringIsNumber(listItem);
            }
            case 'list[bool]': {
                return (
                    defaultCondition &&
                    ['true', 'false'].includes(listItem.toLowerCase())
                );
            }
            default:
                return defaultCondition;
        }
    };

    // @ts-expect-error [TS migration] Was not detected by flow
    const parseListItem = (listItem) => {
        setIsError(false);
        switch (parameterType) {
            case 'list[number]': {
                if (stringIsNumber(listItem)) return Number(listItem);
                setIsError(true);
                return null;
            }
            case 'list[bool]': {
                if (['true', 'false'].includes(listItem.toLowerCase()))
                    return Boolean(listItem.toLowerCase());
                setIsError(true);
                return null;
            }
            default:
                return listItem;
        }
    };
    // @ts-expect-error [TS migration] Was not detected by flow
    const onChange = (enteredValues) => {
        if (Array.isArray(enteredValues)) {
            const parsedEnteredValues = Array.from(enteredValues)
                .map(parseListItem)
                .filter((e) => e !== null);
            setParameterValue(parameterName, parsedEnteredValues);
            setInputValues(parsedEnteredValues);
        }
    };

    return (
        <Autocomplete
            multiple
            id="tags-filled"
            className="parameter-value"
            limitTags={2}
            options={[]}
            // @ts-expect-error [TS migration] Was not detected by flow
            defaultValue={inputValues}
            margin="none"
            onChange={(event, enteredValues) => onChange(enteredValues)}
            freeSolo
            renderTags={(enteredValues, getTagProps) =>
                enteredValues
                    .filter(filterEnteredValueType)
                    .map((option, index) => (
                        // key is defined in getTagProps
                        // eslint-disable-next-line react/jsx-key
                        <Chip
                            variant="outlined"
                            label={option}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...getTagProps({ index })}
                        />
                    ))
            }
            renderInput={(params) => (
                <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    onChange={onChange}
                    className="text"
                    error={isError}
                    label={isError && t('errors.invalidInput')}
                    variant="outlined"
                    margin="dense"
                    size="small"
                />
            )}
        />
    );
};

export default ParameterListInput;
